import * as React from 'react';
import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useQuery } from 'react-query';
import apiClient from '../../request/http-common';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { 
    Button, 
    TextField, 
    Box, 
    InputAdornment, 
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { motion } from 'framer-motion';

export default function ListReferrals() {
    const [referrals, setReferrals] = useState(null);
    const [filteredReferrals, setFilteredReferrals] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [investments, setInvestments] = useState([]);
    
    // Dialog states
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
    const [selectedReferral, setSelectedReferral] = useState(null);
    const [openStatsDialog, setOpenStatsDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Form states
    const [formData, setFormData] = useState({
        inviter_id: '',
        invited_id: '',
        earning: '',
        status: 'pending',
        investment_id: ''
    });

    const { isLoading: isLoadingReferrals, refetch: getReferrals } = useQuery(
        "load-admin-referrals",
        async () => {
            return await apiClient.get("/api/admin/referrals");
        },
        {
            enabled: false,
            onSuccess: (res) => {
                if (res.data.status === 'success') {
                    setReferrals(res.data.referrals);
                    setFilteredReferrals(res.data.referrals);
                    const event2 = new Event('processed');
                    window.dispatchEvent(event2);
                }
            },
            onError: (err) => {
                console.error(err.response?.data || err);
                const event2 = new Event('processed');
                window.dispatchEvent(event2);
            },
        }
    );

    const { isLoading: isLoadingStats, refetch: getStats } = useQuery(
        "load-referral-stats",
        async () => {
            return await apiClient.get("/api/admin/referrals/stats");
        },
        {
            enabled: false,
            onSuccess: (res) => {
                if (res.data.status === 'success') {
                    setStats(res.data.stats);
                }
            },
        }
    );

    const { isLoading: isLoadingFormData, refetch: getFormData } = useQuery(
        "load-referral-form-data",
        async () => {
            return await apiClient.get("/api/admin/referrals/create");
        },
        {
            enabled: false,
            onSuccess: (res) => {
                if (res.data.status === 'success') {
                    setUsers(res.data.users);
                    setInvestments(res.data.investments);
                }
            },
        }
    );

    useEffect(() => {
        getReferrals();
        getStats();
        if (isLoadingReferrals) {
            const event3 = new Event('processing');
            window.dispatchEvent(event3);
        }
    }, []);

    // Search functionality
    const handleSearch = (event) => {
        const term = event.target.value.toLowerCase();
        setSearchTerm(term);
        
        if (!term) {
            setFilteredReferrals(referrals);
        } else {
            const filtered = referrals.filter(referral => 
                referral.inviter?.name.toLowerCase().includes(term) ||
                referral.invited?.name.toLowerCase().includes(term) ||
                referral.inviter?.email.toLowerCase().includes(term) ||
                referral.invited?.email.toLowerCase().includes(term) ||
                referral.status.toLowerCase().includes(term) ||
                referral.earning.toString().includes(term)
            );
            setFilteredReferrals(filtered);
        }
    };

    const handleOpenDialog = (mode, referral = null) => {
        setDialogMode(mode);
        setSelectedReferral(referral);
        
        if (mode === 'edit' && referral) {
            setFormData({
                inviter_id: referral.inviter_id,
                invited_id: referral.invited_id,
                earning: referral.earning,
                status: referral.status,
                investment_id: referral.investment_id || ''
            });
            // For edit mode, don't fetch form data - we only need earning and status
            setOpenDialog(true);
        } else {
            setFormData({
                inviter_id: '',
                invited_id: '',
                earning: '',
                status: 'pending',
                investment_id: ''
            });
            // Only fetch form data for create mode
            getFormData();
            setOpenDialog(true);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedReferral(null);
        setIsSubmitting(false);
        setFormData({
            inviter_id: '',
            invited_id: '',
            earning: '',
            status: 'pending',
            investment_id: ''
        });
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            const event3 = new Event('processing');
            window.dispatchEvent(event3);

            let response;
            if (dialogMode === 'create') {
                response = await apiClient.post('/api/admin/referrals', formData);
            } else {
                // For edit mode, only send the editable fields
                const editData = {
                    earning: formData.earning,
                    status: formData.status
                };
                response = await apiClient.put(`/api/admin/referrals/${selectedReferral.id}`, editData);
            }

            if (response.data.status === 'success') {
                localStorage.setItem('notification', JSON.stringify({
                    type: 'success',
                    message: response.data.message
                }));
                const event = new Event('newMessage');
                window.dispatchEvent(event);
                
                handleCloseDialog();
                getReferrals();
                getStats();
            }
        } catch (error) {
            localStorage.setItem('notification', JSON.stringify({
                type: 'error',
                message: error.response?.data?.message || 'An error occurred'
            }));
            const event = new Event('newMessage');
            window.dispatchEvent(event);
        } finally {
            setIsSubmitting(false);
            const event2 = new Event('processed');
            window.dispatchEvent(event2);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this referral?')) {
            try {
                const event3 = new Event('processing');
                window.dispatchEvent(event3);

                const response = await apiClient.delete(`/api/admin/referrals/${id}`);
                
                if (response.data.status === 'success') {
                    localStorage.setItem('notification', JSON.stringify({
                        type: 'success',
                        message: response.data.message
                    }));
                    const event = new Event('newMessage');
                    window.dispatchEvent(event);
                    
                    getReferrals();
                    getStats();
                }
            } catch (error) {
                localStorage.setItem('notification', JSON.stringify({
                    type: 'error',
                    message: error.response?.data?.message || 'Failed to delete referral'
                }));
                const event = new Event('newMessage');
                window.dispatchEvent(event);
            } finally {
                const event2 = new Event('processed');
                window.dispatchEvent(event2);
            }
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'success';
            case 'declined': return 'error';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    const getDate = (date) => new Date(date).toDateString();
    const formatCurrency = (amount) => `$${parseFloat(amount).toFixed(2)}`;

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ marginTop: '75px' }}
        >
            <Container>
                <Typography 
                    mt={4} 
                    mb={2} 
                    sx={{ 
                        fontWeight: 'bold', 
                        fontSize: 24, 
                        textAlign: 'center', 
                        textTransform: 'uppercase',
                        color: 'primary.main'
                    }} 
                    gutterBottom 
                    variant="h4" 
                    component="div"
                >
                    {isLoadingReferrals ? 'Loading Referrals...' : 'Referral Management'}
                </Typography>

                {/* Action Buttons */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog('create')}
                        sx={{ 
                            background: 'linear-gradient(45deg, #22c55e, #16a34a)',
                            '&:hover': { background: 'linear-gradient(45deg, #16a34a, #15803d)' }
                        }}
                    >
                        Create Referral
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setOpenStatsDialog(true)}
                        sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                    >
                        View Statistics
                    </Button>
                </Box>

                {/* Search Box */}
                <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <TextField
                        placeholder="Search referrals by inviter, invited user, email, status, or earning..."
                        value={searchTerm}
                        onChange={handleSearch}
                        variant="outlined"
                        sx={{ minWidth: 500, mb: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {filteredReferrals && (
                        <Typography variant="body2" color="text.secondary">
                            {searchTerm ? 
                                `Found ${filteredReferrals.length} referral${filteredReferrals.length !== 1 ? 's' : ''} matching "${searchTerm}"` :
                                `Showing all ${filteredReferrals.length} referrals`
                            }
                        </Typography>
                    )}
                </Box>

                <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
                    {filteredReferrals !== null && (
                        <Table sx={{ minWidth: '100%' }} size="small" aria-label="referrals table">
                            <TableHead sx={{ backgroundColor: 'primary.main' }}>
                                <TableRow>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ID</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Inviter</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Invited User</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Earning</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Investment</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Created</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredReferrals.map((referral) => (
                                    <TableRow
                                        key={referral.id}
                                        sx={{ 
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            '&:hover': { backgroundColor: 'rgba(34, 197, 94, 0.05)' }
                                        }}
                                    >
                                        <TableCell component="th" scope="row">
                                            #{referral.id}
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {referral.inviter?.name || 'N/A'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {referral.inviter?.email || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {referral.invited?.name || 'N/A'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {referral.invited?.email || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                                                {formatCurrency(referral.earning)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={referral.status.toUpperCase()} 
                                                color={getStatusColor(referral.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {referral.investment ? (
                                                <Typography variant="caption">
                                                    ${referral.investment.amount}
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    No Investment
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{getDate(referral.created_at)}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title="Edit Referral">
                                                    <IconButton 
                                                        size="small" 
                                                        color="primary"
                                                        onClick={() => handleOpenDialog('edit', referral)}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Referral">
                                                    <IconButton 
                                                        size="small" 
                                                        color="error"
                                                        onClick={() => handleDelete(referral.id)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>

                {/* Create/Edit Dialog */}
                <Dialog open={openDialog} onClose={isSubmitting ? null : handleCloseDialog} maxWidth="md" fullWidth>
                    <DialogTitle>
                        {dialogMode === 'create' ? 'Create New Referral' : 'Edit Referral'}
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {dialogMode === 'create' ? (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Inviter</InputLabel>
                                            <Select
                                                value={formData.inviter_id}
                                                onChange={(e) => handleFormChange('inviter_id', e.target.value)}
                                                label="Inviter"
                                            >
                                                {users.map((user) => (
                                                    <MenuItem key={user.id} value={user.id}>
                                                        {user.name} ({user.email})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Invited User</InputLabel>
                                            <Select
                                                value={formData.invited_id}
                                                onChange={(e) => handleFormChange('invited_id', e.target.value)}
                                                label="Invited User"
                                            >
                                                {users.filter(user => user.id !== formData.inviter_id).map((user) => (
                                                    <MenuItem key={user.id} value={user.id}>
                                                        {user.name} ({user.email})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel>Investment (Optional)</InputLabel>
                                            <Select
                                                value={formData.investment_id}
                                                onChange={(e) => handleFormChange('investment_id', e.target.value)}
                                                label="Investment (Optional)"
                                            >
                                                <MenuItem value="">No Investment</MenuItem>
                                                {investments.map((investment) => (
                                                    <MenuItem key={investment.id} value={investment.id}>
                                                        ${investment.amount} - {investment.user?.name} ({investment.status})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    {/* Read-only fields for edit mode */}
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Inviter"
                                            value={selectedReferral?.inviter?.name + ' (' + selectedReferral?.inviter?.email + ')'}
                                            disabled
                                            variant="filled"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Invited User"
                                            value={selectedReferral?.invited?.name + ' (' + selectedReferral?.invited?.email + ')'}
                                            disabled
                                            variant="filled"
                                        />
                                    </Grid>
                                </>
                            )}
                            
                            {/* Editable fields for both modes */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Earning Amount"
                                    type="number"
                                    value={formData.earning}
                                    onChange={(e) => handleFormChange('earning', e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={formData.status}
                                        onChange={(e) => handleFormChange('status', e.target.value)}
                                        label="Status"
                                    >
                                        <MenuItem value="pending">Pending</MenuItem>
                                        <MenuItem value="approved">Approved</MenuItem>
                                        <MenuItem value="declined">Declined</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} disabled={isSubmitting}>Cancel</Button>
                        <Button 
                            onClick={handleSubmit} 
                            variant="contained"
                            disabled={
                                isSubmitting || (
                                    dialogMode === 'create' 
                                        ? (!formData.inviter_id || !formData.invited_id || !formData.earning)
                                        : (!formData.earning)
                                )
                            }
                            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                            sx={{
                                background: isSubmitting 
                                    ? 'linear-gradient(45deg, #9ca3af, #6b7280)' 
                                    : 'linear-gradient(45deg, #22c55e, #16a34a)',
                                '&:hover': { 
                                    background: isSubmitting 
                                        ? 'linear-gradient(45deg, #9ca3af, #6b7280)' 
                                        : 'linear-gradient(45deg, #16a34a, #15803d)' 
                                }
                            }}
                        >
                            {isSubmitting 
                                ? (dialogMode === 'create' ? 'Creating...' : 'Updating...') 
                                : (dialogMode === 'create' ? 'Create' : 'Update')
                            }
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Statistics Dialog */}
                <Dialog open={openStatsDialog} onClose={() => setOpenStatsDialog(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Referral Statistics</DialogTitle>
                    <DialogContent>
                        {stats && (
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="primary">Total Referrals</Typography>
                                            <Typography variant="h4">{stats.total_referrals}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="success.main">Approved Referrals</Typography>
                                            <Typography variant="h4">{stats.approved_referrals}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="error.main">Declined</Typography>
                                            <Typography variant="h4">{stats.declined_referrals}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="warning.main">Pending</Typography>
                                            <Typography variant="h4">{stats.pending_referrals}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="primary">Total Earnings</Typography>
                                            <Typography variant="h4">{formatCurrency(stats.total_earnings)}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                {stats.top_inviters?.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mb: 2 }}>Top Inviters</Typography>
                                        <TableContainer component={Paper}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Email</TableCell>
                                                        <TableCell>Referrals</TableCell>
                                                        <TableCell>Earnings</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {stats.top_inviters.map((inviter, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{inviter.inviter?.name}</TableCell>
                                                            <TableCell>{inviter.inviter?.email}</TableCell>
                                                            <TableCell>{inviter.referral_count}</TableCell>
                                                            <TableCell>{formatCurrency(inviter.total_earnings)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenStatsDialog(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </motion.div>
    );
}
